export type OwnerGateStatus =
  | 'resolving'
  | 'auth-required'
  | 'ok'
  | 'connection-error'
  | 'switch-pending'
  | 'unidentified-content'
  | 'storage-unreadable'
  | 'client-update-required';

export type OwnerBootstrapResolution =
  | 'pending'
  | 'installed'
  | 'connection-error'
  | 'switch-pending'
  | 'unidentified-content'
  | 'storage-unreadable'
  | 'client-update-required';

export interface OwnerGateDecisionInput {
  bindingLoaded: boolean;
  /**
   * The durable binding could not be read. This is never "unbound": the
   * profile may hold another account's content, so it stays closed.
   */
  bindingUnreadable: boolean;
  sessionLoaded: boolean;
  sessionOwnerId: string | null;
  bindingOwnerId: string | null;
  matchingBindingReady: boolean;
  bootstrapResolution: OwnerBootstrapResolution;
}

/** `unreadable` marks a failed binding read; its `value` is then always null. */
export type LoadedBinding = { value: string | null; unreadable?: true };
export type LoadedSession = { userId: string | null };

/**
 * Preserve snapshot identity when a durable reread confirms the same owner.
 * The owner-gate effects key off these objects, so replacing an equal snapshot
 * can cancel and restart an in-flight cleanup or adoption forever.
 */
export function retainLoadedBinding(
  current: LoadedBinding | null,
  value: string | null,
): LoadedBinding {
  return current?.value === value && current.unreadable !== true
    ? current
    : { value };
}

/** Same identity guard for repeated binding read failures. */
export function retainUnreadableBinding(current: LoadedBinding | null): LoadedBinding {
  return current?.unreadable === true ? current : { value: null, unreadable: true };
}

/** Same semantic-identity guard for auth refreshes that keep one user. */
export function retainLoadedSession(
  current: LoadedSession | null,
  userId: string | null,
): LoadedSession {
  return current?.userId === userId ? current : { userId };
}

/** Pure owner-gate decision shared by the hook and its complete regression matrix. */
export function decideOwnerGateStatus(input: OwnerGateDecisionInput): OwnerGateStatus {
  if (!input.bindingLoaded || !input.sessionLoaded) return 'resolving';
  if (input.sessionOwnerId === null) return 'auth-required';
  // Before any owner comparison: an unreadable binding can never open,
  // adopt or switch, whatever a stale bootstrap result says.
  if (input.bindingUnreadable) return 'storage-unreadable';
  if (input.bindingOwnerId === input.sessionOwnerId) {
    return input.matchingBindingReady ? 'ok' : 'resolving';
  }
  if (input.bootstrapResolution === 'installed') return 'ok';
  if (input.bootstrapResolution === 'connection-error') return 'connection-error';
  if (input.bootstrapResolution === 'switch-pending') return 'switch-pending';
  if (input.bootstrapResolution === 'unidentified-content') return 'unidentified-content';
  if (input.bootstrapResolution === 'storage-unreadable') return 'storage-unreadable';
  if (input.bootstrapResolution === 'client-update-required') return 'client-update-required';
  return 'resolving';
}

/** What one user-requested storage recheck established. */
export type OwnerStorageRecheckOutcome =
  /** The account or owner transition changed meanwhile: report nothing. */
  | { kind: 'stale' }
  | { kind: 'client-update-required' }
  /** Still not inspectable; the screen stays and says so. */
  | { kind: 'unreadable'; stage: 'binding' | 'inspection' }
  /** Storage reads again: hand the binding back to the normal gate. */
  | { kind: 'readable'; binding: string | null };

export interface OwnerStorageRecheckDeps {
  readBinding: () => Promise<string | null>;
  inspectUnbound: () => Promise<'empty' | 'protected' | 'unreadable'>;
  /**
   * No change of the session owner (A to B to A included) and no
   * owner-transition barrier change since the recheck started.
   */
  isCurrent: () => boolean;
  isClientUpdateRequiredError: (error: unknown) => boolean;
}

/**
 * Start one user-requested storage recheck, or nothing when one is already
 * running. The guard closes synchronously, before the first await, so a
 * second tap in the same moment cannot start an overlapping attempt. The
 * recheck only reads: it repeats the binding read and, for an unbound
 * profile, the inspection. It never binds, adopts or decides access; a
 * readable result goes back through the normal gate, which repeats its own
 * checks under the owner-transition barrier and Web Locks. A result reached
 * after the account or owner transition changed is `stale` and is discarded.
 */
export function startOwnerStorageRecheck(
  guard: { inFlight: boolean },
  deps: OwnerStorageRecheckDeps,
): Promise<OwnerStorageRecheckOutcome> | null {
  if (guard.inFlight) return null;
  guard.inFlight = true;
  return recheckOwnerStorage(deps).finally(() => {
    guard.inFlight = false;
  });
}

async function recheckOwnerStorage(
  deps: OwnerStorageRecheckDeps,
): Promise<OwnerStorageRecheckOutcome> {
  let binding: string | null;
  try {
    binding = await deps.readBinding();
  } catch (error) {
    if (!deps.isCurrent()) return { kind: 'stale' };
    if (deps.isClientUpdateRequiredError(error)) return { kind: 'client-update-required' };
    return { kind: 'unreadable', stage: 'binding' };
  }
  if (!deps.isCurrent()) return { kind: 'stale' };
  if (binding === null) {
    let profile: 'empty' | 'protected' | 'unreadable';
    try {
      profile = await deps.inspectUnbound();
    } catch {
      profile = 'unreadable';
    }
    if (!deps.isCurrent()) return { kind: 'stale' };
    if (profile === 'unreadable') return { kind: 'unreadable', stage: 'inspection' };
  }
  return { kind: 'readable', binding };
}

/** Dormant support-tool states retained for the encrypted backup component. */
export type OwnerRecoveryStatus =
  | 'known-owner-mismatch'
  | 'maintenance'
  | 'unidentified-content'
  | 'unidentified-content-read-only';

export interface OwnerRecoveryOptionsInput {
  status: OwnerRecoveryStatus;
  backupVerified: boolean;
  accountCheckFailed: boolean;
}

export interface OwnerRecoveryOptions {
  canChooseAccount: boolean;
  canSaveBackup: boolean;
  showSeparateProfileHelp: boolean;
  canSignOut: true;
  canEnterApp: false;
  backupReady: boolean;
  showAccountCheckFailed: boolean;
}

/**
 * Backup completion is evidence that a copy was saved, never authority to
 * enter, bind, replace, merge, move, archive, restore, or delete content.
 */
export function ownerRecoveryOptions(
  input: OwnerRecoveryOptionsInput,
): OwnerRecoveryOptions {
  const canChooseAccount =
    input.status === 'known-owner-mismatch' ||
    input.status === 'unidentified-content';
  return {
    canChooseAccount,
    canSaveBackup:
      input.status === 'unidentified-content' ||
      input.status === 'unidentified-content-read-only',
    showSeparateProfileHelp: input.status === 'known-owner-mismatch',
    canSignOut: true,
    canEnterApp: false,
    backupReady: input.backupVerified,
    showAccountCheckFailed: canChooseAccount && input.accountCheckFailed,
  };
}
